/** A custom HTTP header sent with every call of a webhook. Mirrors the API's V_5.0 WebhookHeader. */
export interface IWebhookHeader {
    name?: string;
    /** Absent on read for secret headers: the API never returns a stored secret. */
    value?: string;
    /** Secret headers are write-only; the value is hidden after saving. */
    secret?: boolean;
    /** Read-only. True when the header has a stored value; for a secret header this is the only sign of one. */
    hasValue?: boolean;
}

export interface IWebhook {
    id?: string;
    name?: string;
    type?: string;
    url?: string;
    globalEnabled?: boolean;
    enabledIds?: string[];
    lastUpdated?: Date;
    requestBody?: string;
    vendor?: string;
    /** Omitted on save leaves stored headers alone; an empty array removes them all. */
    headers?: IWebhookHeader[];
    /** When set and in the future, debug logging (full request/response capture) is active until this time. */
    debugUntil?: string;
}

/** A single record of a webhook being invoked. Mirrors the API's V_7.0 WebhookActivity. */
export interface IWebhookActivity {
    id: string;
    timestamp: string;
    webhookId?: string;
    webhookName?: string;
    webhookType?: string;
    url?: string;
    success: boolean;
    statusCode?: number;
    error?: string;
    usedCustomBody: boolean;
    referenceType?: string;
    referenceId?: string;
    /** Only populated for calls made while debug mode was enabled. Newline-separated "Name: value" lines, secrets redacted by the server. */
    requestHeaders?: string;
    /** Only populated for calls made while debug mode was enabled. */
    requestBody?: string;
    /** Only populated for calls made while debug mode was enabled. */
    responseBody?: string;
}

export const WEBHOOK_TYPE_SF_ORDER = 'sf_order';
export const WEBHOOK_TYPE_SF_QUOTE_REQUEST = 'sf_quote_request';
export const WEBHOOK_TYPE_SF_CART_COMMENT = 'sf_cart_comment';
export const WEBHOOK_TYPE_SF_SIGNUP = 'sf_signup';
export const WEBHOOK_TYPE_SF_SIGNUP_APPROVAL = 'sf_signup_approval';
export const WEBHOOK_TYPE_DPL_EXPORT = 'dpl_export';
export const WEBHOOK_TYPE_EXTENSION_ERROR = 'extension_error';

export const WEBHOOK_TYPE_PROCUREMENT_ORDER = 'procurement_order';
export const WEBHOOK_TYPE_PROCUREMENT_DEMAND = 'procurement_demand_created';
export const WEBHOOK_TYPE_PROCUREMENT_ORDER_UPDATE = 'procurement_order_update';
export const WEBHOOK_TYPE_PROCUREMENT_ORDER_SHIPMENT_UPDATE = 'procurement_order_shipment_update';

/**
 * Webhook types scoped to a specific storefront, whose "enabled ids" are storefront ids. Extracted
 * from the ids dropdown's inline check so a new storefront event only has to be listed once.
 */
export const STOREFRONT_WEBHOOK_TYPES = [
    WEBHOOK_TYPE_SF_ORDER,
    WEBHOOK_TYPE_SF_QUOTE_REQUEST,
    WEBHOOK_TYPE_SF_CART_COMMENT,
    WEBHOOK_TYPE_SF_SIGNUP,
    WEBHOOK_TYPE_SF_SIGNUP_APPROVAL,
];

export const PROCUREMENT_WEBHOOK_TYPES = [
    WEBHOOK_TYPE_PROCUREMENT_ORDER,
    WEBHOOK_TYPE_PROCUREMENT_DEMAND,
    WEBHOOK_TYPE_PROCUREMENT_ORDER_UPDATE,
    WEBHOOK_TYPE_PROCUREMENT_ORDER_SHIPMENT_UPDATE,
];